import { useEffect, useRef } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";

/**
 * Bug #38472: shared focus-management behaviours for the two hand-rolled Quick
 * Entry modal primitives (`MatchGridModal`, `CreateEntityFormModal`), which
 * implemented none of the four behaviours a managed dialog owes: initial focus,
 * a Tab trap, Escape-to-close, and focus restoration on close. Colocated here
 * (not a new top-level `hooks/` directory - the SPA has none today) since these
 * two consumers are its only callers.
 *
 * Business rules (Custom.Spec on Bug #38472):
 *   1. On mount: record the opener, then focus the dialog root's first tabbable
 *      descendant. The opener is `document.activeElement` - EXCEPT when that is
 *      `null`, `document.body`, or `document.documentElement` (nothing meaningful
 *      is focused), in which case the dialog root's OWN parent element is
 *      recorded instead, as a synthetic fallback anchor. Which branch was taken
 *      is flagged at capture time (`usedFallbackOpenerRef`) and read back - never
 *      re-derived - by Rule 6's cleanup.
 *   2. No tabbable descendant -> the root itself takes focus (requires the
 *      caller to render the root with `tabIndex={-1}`, so it is a valid focus
 *      target), so focus is never left in the background - AND Tab from the
 *      root is trapped (`preventDefault()`), not just focused: focus alone
 *      would still let the browser's own default Tab walk into the page
 *      behind the dialog on the very first keypress (PR #10205 human review).
 *   3. Tab on the last tabbable descendant -> wraps to the first.
 *   4. Shift+Tab on the first tabbable descendant - OR on the dialog root
 *      itself - wraps to the last. The root counts as a boundary because Rule
 *      2's `tabIndex={-1}` makes it CLICK-focusable in a real browser: clicking
 *      a non-focusable region (e.g. header whitespace beside the title) walks
 *      focus up to the nearest focusable ancestor, which is the root. Without
 *      this branch, Shift+Tab from there fell through to the browser's own
 *      default action instead of wrapping.
 *   5. Escape -> invokes `onClose`.
 *   6. On unmount, exactly one of three things happens, decided by the
 *      opener's identity as recorded at capture time (Rule 1) - never
 *      re-derived from its current DOM state:
 *        - a REAL, still-connected opener is focused, and its DOM is left
 *          EXACTLY as found - no attribute of it is ever touched;
 *        - a REAL, now-disconnected opener gets no focus call at all;
 *        - the SYNTHETIC fallback anchor (Rule 1) has `tabindex="-1"` stamped
 *          onto it - skipped if it is `document.body`, already matches the
 *          tabbable selector, or already carries a `tabindex` - and is then
 *          focused regardless.
 *      Only the fallback anchor may ever be made programmatically focusable;
 *      a real captured opener must leave the DOM exactly as it was found.
 *   7. The tabbable set for rules 3/4 is recomputed on EVERY keydown, never
 *      cached at mount - `CreateEntityFormModal`'s Save button is conditionally
 *      `disabled`, and `MatchGridModal`'s rows/filter inputs change as filters
 *      are applied.
 *
 * PHI note (hipaa-compliance.md): this hook only moves focus and reads
 * `document.activeElement` - it never logs anything or serialises any model.
 */

/**
 * Deliberately scoped to what MatchGridModal and CreateEntityFormModal actually
 * render today - not a general "every tabbable element" selector. It cannot see
 * `[contenteditable]`, `area[href]`, `<details>`, `iframe`, or
 * `audio/video[controls]`, and - more consequentially - it does not exclude
 * elements that MATCH but are not reachable: `input[type="hidden"]`, `[hidden]`,
 * or anything under `display:none` / `visibility:hidden`. Neither primitive hits
 * this today (checked both: no hidden inputs; `CreateEntityFormModal`'s
 * `<option disabled hidden>` is an `<option>`, which this selector does not match
 * at all). A NEW consumer of this hook must re-check that hazard before reusing
 * this selector as-is: a hidden input rendered first or last inside a future
 * dialog would silently become the wrap target, `.focus()` on it would be a
 * no-op, and Tab at the boundary would appear to do nothing at all -
 * indistinguishable from the bug this hook fixes.
 *
 * That blind-spot list is also what Rule 6's cleanup gate on
 * `usedFallbackOpenerRef` is protecting: an opener this selector cannot see is
 * still genuinely focusable, so `!opener.matches(TABBABLE_SELECTOR)` alone would
 * let `tabindex="-1"` be stamped onto a REAL captured opener and evict it from
 * the Tab order - the exact `2f5688f` harm, reached through the one class of
 * element this selector is blind to. Test 4.5 pins that clause, with an
 * `<iframe>` opener: focusable in jsdom exactly as in a browser
 * (`isFocusableAreaElement` returns true for `iframe` unconditionally, in the
 * 11.12.0 copy and in the 14.1.0 one CRA's jest actually runs), while matching
 * neither this selector nor any `tabindex` - so it reaches the gate that test
 * 4.4's `<button>` opener, which MATCHES the selector, cannot. Forcing
 * `usedFallbackOpenerRef.current` to `true` fails 4.5 and only 4.5. An `iframe`
 * is the one member of the blind-spot list that can carry such a test:
 * `[contenteditable]`, `area[href]` and `audio/video[controls]` are all
 * genuinely unfocusable in jsdom.
 */
const TABBABLE_SELECTOR = "a[href], button, input, select, textarea, [tabindex]";

/** True for an element the tab order would actually reach - excludes `disabled` controls and any explicit `tabindex="-1"` (Business Rule 7). */
function isTabbable(el: HTMLElement): boolean {
    if (el.hasAttribute("disabled")) {
        return false;
    }
    const tabIndexAttr = el.getAttribute("tabindex");
    return tabIndexAttr === null || Number(tabIndexAttr) >= 0;
}

/** The dialog root's tabbable descendants, in DOM order. Recomputed on every call - never cached (Business Rule 7). */
function getTabbableDescendants(root: HTMLElement): readonly HTMLElement[] {
    return Array.from(root.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)).filter(isTabbable);
}

export interface ModalFocusHandle {
    /** Attach to the dialog root (the `role="dialog"` element). Also needs `tabIndex={-1}` on that element for Business Rule 2. */
    readonly ref: React.RefObject<HTMLDivElement>;
    /** Attach to the SAME dialog root's `onKeyDown` (React synthetic - never a `document`-level listener, so nothing can leak once the dialog unmounts). */
    readonly onKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
}

export function useModalFocus(onClose: () => void): ModalFocusHandle {
    const ref = useRef<HTMLDivElement>(null);
    const openerRef = useRef<Element | null>(null);
    // Tracks WHICH branch captured `openerRef.current` - true only for the
    // parentElement fallback (round-2 review). The cleanup must never mutate a
    // real captured opener's attributes, only this synthetic anchor's, so the
    // branch decision has to survive from capture time to cleanup time rather
    // than being re-derived (re-deriving from "no tabindex attribute" is exactly
    // the bug: that is also true of a plain <button> that was never given one).
    const usedFallbackOpenerRef = useRef(false);
    // Always call the LATEST onClose without re-running the mount effect below.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        const root = ref.current;
        if (!root) {
            return undefined;
        }

        // Rule 1: record the opener, then move focus into the dialog.
        //
        // Bug #38472 round-1 review (Warning 1): PatientInfoRow's manual-search path
        // clears its own `searchMode` - unmounting the inline Search button the
        // specialist just clicked - BEFORE the awaited search resolves and this
        // dialog ever mounts. By the time this effect runs, that button is already
        // gone and `document.activeElement` has already collapsed to `document.body`.
        // There is no way to recover the destroyed button from here, so when the
        // captured element is exactly `body` (a signal that nothing meaningful is
        // focused, not a real opener), fall back to this dialog's OWN parent node -
        // guaranteed to still be in the document when the dialog unmounts, since only
        // the dialog itself is conditionally rendered, never its parent. This is
        // deliberately generic (no consumer-specific selector) so it works for any
        // current or future caller. It never engages on the flow the bug actually
        // reports: Assign Contact's opener (`.requestor-info-row__assign-contact-btn`)
        // is a real, persistently-connected button, never `body`, at capture time.
        //
        // Round-2 review: "nothing focused" is also tested against `documentElement`
        // (the `<html>` element), not just `body`. Per spec, and confirmed in both
        // jsdom and Chromium, `document.activeElement` reports `body` (never
        // `documentElement`) when nothing is focused in an HTML document - so this
        // is not a live gap - but `documentElement` is the ONE input value that
        // would otherwise silently route a fallback case down the real-opener
        // branch and leave focus recoverable to nowhere, so it costs nothing to
        // treat the same as `body` here.
        const active = document.activeElement;
        const usedFallbackOpener = !active || active === document.body || active === document.documentElement;
        usedFallbackOpenerRef.current = usedFallbackOpener;
        openerRef.current = usedFallbackOpener ? root.parentElement : active;
        const [first] = getTabbableDescendants(root);
        if (first) {
            first.focus();
        } else {
            // Rule 2: nothing tabbable - the root itself takes focus (requires
            // the caller's `tabIndex={-1}`) so focus is never left behind.
            root.focus();
        }

        return () => {
            // Rule 6: restore focus to the opener, unless it's no longer
            // connected to the document (e.g. the grid's own "+ Add new
            // Contact" button, unmounted the instant the form replaces it).
            const opener = openerRef.current;
            if (opener instanceof HTMLElement && opener.isConnected) {
                // Round-2 review regression fix: ONLY the parentElement fallback
                // (usedFallbackOpenerRef, decided at capture time above - never
                // re-derived here) is a plain container with no native focus target,
                // so `tabindex="-1"` is safe to add there (script-focusable, never
                // joins the natural Tab order, since that requires >= 0). A REAL
                // captured opener must be left exactly as found: `!hasAttribute
                // ("tabindex")` is ALSO true of a plain, natively-focusable <button>
                // that simply never got an explicit tabIndex prop (e.g. the actually
                // reported flow's `.requestor-info-row__assign-contact-btn`) - gating
                // on that alone, as an earlier version of this fix did, permanently
                // evicted such a button from the Tab order on every close, which is
                // the same class of keyboard-inaccessibility bug #38472 exists to fix.
                //
                // The fallback anchor's tabindex is left in place after use rather
                // than stripped back off: -1 never joins the natural Tab order, so a
                // leftover -1 on a non-interactive container affects no other keyboard
                // flow, and removing it would need extra bookkeeping for no benefit.
                //
                // Round-2 review Suggestion 3: even within the fallback branch, never
                // stamp tabindex on an anchor that is ALREADY focusable one way or
                // another - `document.body` (`.focus()` on body is a natural no-op;
                // nothing to gain from a tabindex there) or a node that already
                // matches `TABBABLE_SELECTOR` (a real link/button/input/etc. as the
                // dialog's own parent - unusual, but possible). Stamping `-1` on an
                // already-focusable ancestor would reproduce the EXACT `2f5688f`
                // eviction harm one level up, at the parent instead of the opener.
                // We shipped that bug once; this makes it structurally unavailable
                // rather than relying on the fallback anchor happening to be a plain,
                // non-interactive container every time.
                if (
                    usedFallbackOpenerRef.current
                    && opener !== document.body
                    && !opener.matches(TABBABLE_SELECTOR)
                    && !opener.hasAttribute("tabindex")
                ) {
                    opener.setAttribute("tabindex", "-1");
                }
                opener.focus();
            }
        };
        // Mount/unmount only - the opener is captured once, at mount. No suppression
        // needed: this effect closes only over refs (which react-hooks/exhaustive-deps
        // ignores by design) and the module-scope getTabbableDescendants - verified by
        // removing the disable comment and confirming no warning is raised.
    }, []);

    const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
        if (event.key === "Escape") {
            // Rule 5. Deliberately no `preventDefault()` and no
            // `stopPropagation()`: this is a React synthetic handler on the dialog
            // root, the SPA's own code registers no `document`- or `window`-level
            // keydown listener anywhere, and nothing between this root and the
            // document has an Escape handler (checked across `src`) - so there is
            // nothing above us for a stopped or defaulted-away Escape to protect,
            // and preventDefault() would suppress the browser's own Escape actions
            // we want left alone. Two residual cases, recorded so a future change
            // weighs them rather than rediscovering them:
            //   - Escape with `CreateEntityFormModal`'s native `<select>` popup
            //     open: in a browser that delivers that keydown to the page rather
            //     than consuming it in the popup, one keypress both dismisses the
            //     dropdown and closes the dialog. The popup's open state is not
            //     observable from here, so this is recorded, not worked around.
            //   - any ancestor keydown handler added later WILL see this event.
            //     The nearest one today, `.right-pane`'s `onKeyDown`
            //     (DataEntryDetailsRightPane), is never an ancestor of these
            //     dialogs - it is the other arm of DataEntryDetails' Quick Entry
            //     ternary - but that is a layout fact, not a guarantee.
            onCloseRef.current();
            return;
        }
        if (event.key !== "Tab") {
            return;
        }
        const root = ref.current;
        if (!root) {
            return;
        }

        // Rule 7: recomputed on every keydown, never cached at mount.
        const tabbable = getTabbableDescendants(root);
        if (tabbable.length === 0) {
            // PR #10205 human review: Rule 2 puts focus on the dialog root when
            // nothing inside is tabbable, but that alone is not a trap - without
            // preventDefault() here, the FIRST Tab from that root falls through to
            // the browser's own default action and walks straight into whatever
            // follows the dialog in document order, the exact escape this hook
            // exists to close. Tab is made inert instead. Unreachable through
            // either shipped primitive today (both always render a header Close
            // button); pinned via the hook-level harness in test 6.4.
            event.preventDefault();
            return;
        }
        const first = tabbable[0];
        const last = tabbable[tabbable.length - 1];
        const active = document.activeElement;

        if (event.shiftKey) {
            // Rule 4: Shift+Tab from the first tabbable element - OR from the
            // dialog root itself - wraps to the last.
            //
            // Independent-validator finding (round 4): the root carries
            // `tabIndex={-1}` for Rule 2, which makes it CLICK-focusable in a real
            // browser - clicking a non-focusable region (e.g. header whitespace
            // beside the title) walks focus up to the nearest focusable ancestor,
            // the root. Without `active === root` here, neither branch of this
            // handler fires when that happens, so the browser's own default
            // Shift+Tab runs - landing on whatever precedes the dialog in
            // DOCUMENT ORDER (a background Quick Entry control, since these
            // dialogs render inline rather than through a portal), with the
            // dialog still open. That is the second half of the reported defect.
            // Forward Tab needs no matching change: the root PRECEDES its own
            // descendants in document order, so the browser's default forward Tab
            // from the root already lands on the first tabbable descendant.
            if (active === first || active === root) {
                event.preventDefault();
                last.focus();
            }
        } else {
            // Rule 3: Tab from the last tabbable element wraps to the first.
            if (active === last) {
                event.preventDefault();
                first.focus();
            }
        }
    };

    return { ref, onKeyDown };
}
